import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Table, Card, Button, Tag, Space, Modal, Input, message, Typography, Image } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { paymentsApi } from '../api/client';

const { Title, Text } = Typography;
const { TextArea } = Input;

export function Payments() {
  const queryClient = useQueryClient();
  const [selectedPayment, setSelectedPayment] = useState<any>(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectNote, setRejectNote] = useState('');

  const { data: payments, isLoading } = useQuery({
    queryKey: ['pendingPayments'],
    queryFn: async () => {
      const res = await paymentsApi.getPending();
      return res.data.data;
    },
  });

  const confirmMutation = useMutation({
    mutationFn: (paymentId: string) =>
      paymentsApi.confirm(paymentId, 'admin'), // TODO: Get actual admin ID
    onSuccess: () => {
      message.success("To'lov tasdiqlandi!");
      queryClient.invalidateQueries({ queryKey: ['pendingPayments'] });
    },
    onError: () => {
      message.error('Xatolik yuz berdi');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ paymentId, note }: { paymentId: string; note: string }) =>
      paymentsApi.reject(paymentId, 'admin', note),
    onSuccess: () => {
      message.success("To'lov rad etildi");
      queryClient.invalidateQueries({ queryKey: ['pendingPayments'] });
      setRejectModalOpen(false);
      setRejectNote('');
    },
    onError: () => {
      message.error('Xatolik yuz berdi');
    },
  });

  const columns = [
    {
      title: 'Foydalanuvchi',
      key: 'user',
      render: (_: any, record: any) => (
        <Text>@{record.user?.username || record.user?.firstName || 'Noma\'lum'}</Text>
      ),
    },
    {
      title: 'Mashina',
      key: 'car',
      render: (_: any, record: any) => (
        <Text>{record.car?.brand} {record.car?.model}</Text>
      ),
    },
    {
      title: 'Summa',
      key: 'amount',
      render: (_: any, record: any) => (
        <Text strong>{Number(record.amount).toLocaleString()} {record.currency}</Text>
      ),
    },
    {
      title: 'Holat',
      key: 'status',
      render: (_: any, record: any) => (
        <Tag color={record.isFreePass ? 'green' : 'orange'}>
          {record.isFreePass ? 'Bepul' : 'Kutilmoqda'}
        </Tag>
      ),
    },
    {
      title: 'Chek',
      key: 'receipt',
      render: (_: any, record: any) => (
        record.receiptImage ? (
          <Image
            src={`/api/payments/image/${record.receiptImage}`}
            width={50}
            height={50}
            style={{ objectFit: 'cover', borderRadius: 4 }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
          />
        ) : (
          <Text type="secondary">Yo'q</Text>
        )
      ),
    },
    {
      title: 'Sana',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('uz-UZ'),
    },
    {
      title: 'Amallar',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button
            type="primary"
            icon={<CheckOutlined />}
            onClick={() => {
              Modal.confirm({
                title: "To'lovni tasdiqlaysizmi?",
                okText: 'Tasdiqlash',
                cancelText: 'Bekor qilish',
                onOk: () => confirmMutation.mutate(record.id),
              });
            }}
            loading={confirmMutation.isPending}
          >
            Tasdiqlash
          </Button>
          <Button
            danger
            icon={<CloseOutlined />}
            onClick={() => {
              setSelectedPayment(record);
              setRejectModalOpen(true);
            }}
          >
            Rad etish
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={3}>To'lovlar</Title>

      <Table
        columns={columns}
        dataSource={payments || []}
        loading={isLoading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title="To'lovni rad etish"
        open={rejectModalOpen}
        onCancel={() => setRejectModalOpen(false)}
        onOk={() => {
          rejectMutation.mutate({ paymentId: selectedPayment?.id, note: rejectNote });
        }}
        okText="Rad etish"
        cancelText="Bekor qilish"
        confirmLoading={rejectMutation.isPending}
      >
        <TextArea
          rows={4}
          placeholder="Izoh (ixtiyoriy)"
          value={rejectNote}
          onChange={(e) => setRejectNote(e.target.value)}
        />
      </Modal>
    </div>
  );
}
